/**
 * Pure decision logic for turning a `DailyChallengeClient.getMyStatus()`
 * result into what `DailyChallengeScene` should show - no Phaser
 * dependency, so it's directly unit-testable (see
 * `src/daily/__tests__/attemptStatus.test.ts`) without a Scene.
 *
 * The backend (`daily_participation`) is always the authoritative attempt
 * counter. This module's one job is to make sure the UI can never
 * *fabricate* an attempt count: there is deliberately no "assume 3 and
 * carry on" branch here. A `getMyStatus()` call that didn't come back
 * `state: "ok"` with a real `status` payload is always `failed` - the
 * caller must stay on a recoverable loading/retry state rather than
 * rendering any attempts-remaining number (real or guessed).
 */

import type { DailyMyRankResult, DailyStatusResult } from "./DailyChallengeClient";

export type AttemptStatusOutcome =
  | {
      type: "ready" | "no-attempts";
      attemptsRemaining: number;
      bestScore: number | null;
      bestCompleted: boolean;
      hasResult: boolean;
    }
  | { type: "failed"; loadState: "offline" | "error" };

/**
 * `simulatedAttemptsRemaining` is the QA-only override
 * (`dailyQa.getSimulatedAttemptsRemaining`) - still only ever applied on
 * top of a *successful* status load, never as a substitute for one.
 */
export function resolveAttemptStatusOutcome(statusResult: DailyStatusResult, simulatedAttemptsRemaining: number | undefined): AttemptStatusOutcome {
  if (statusResult.state !== "ok" || !statusResult.status) {
    return { type: "failed", loadState: statusResult.state === "offline" ? "offline" : "error" };
  }

  const serverAttemptsRemaining = Math.max(0, 3 - statusResult.status.attemptsReserved);
  const attemptsRemaining = simulatedAttemptsRemaining !== undefined ? simulatedAttemptsRemaining : serverAttemptsRemaining;

  return {
    type: attemptsRemaining > 0 ? "ready" : "no-attempts",
    attemptsRemaining,
    bestScore: statusResult.status.bestScore,
    bestCompleted: statusResult.status.bestCompleted,
    hasResult: statusResult.status.hasResult,
  };
}

/**
 * Whether a genuine, server-verified, ranked, completed Daily V2 best
 * exists right now - the single gate `DailyChallengeScene` uses to decide
 * whether CHALLENGE A FRIEND is shown on the Daily summary/status screen
 * (independent of `attemptsRemaining`/`isReserving` - creating or
 * reopening the share modal never reserves, consumes, restores, or
 * changes an attempt).
 *
 * `has_result` (see `get_my_daily_rank()`'s SQL, migration 005) is only
 * ever true for a participant row that is `best_completed` with a
 * non-null `best_submitted_at` - so this already excludes an unranked
 * (never-played) date, an incomplete/restarted attempt, and a
 * server-rejected submission (none of which ever populate `best_*`).
 * `dailyPoints !== null` additionally excludes a V1/legacy `challenge_id`,
 * which has no scoring profile to have computed a Daily Points value from.
 * A rank-fetch failure (any `state` other than `"ok"`) is treated the same
 * as "no verified best exists" - the button stays hidden rather than
 * guessing. This is the exact same check `ScoreChallengeScene.loadChallenge()`
 * already uses to decide whether a challenge visitor "has a result."
 */
export function hasVerifiedRankedBest(rankResult: DailyMyRankResult): boolean {
  return rankResult.state === "ok" && !!rankResult.rank?.hasResult && rankResult.rank.dailyPoints !== null;
}
